import { Directive, Input, TemplateRef, ViewContainerRef } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AccessPermissionService,
  PermissionsState,
} from '@osos/shared/legacy-core';
import {
  AccessRightLevelEnum,
  DataEntityPresenterModel,
} from '@osos/shared/types';

@Directive({
  selector: '[appHasEditAccess]',
})
export class EditAccessPermissionDirective {
  constructor(
    private store: Store,
    private vcr: ViewContainerRef,
    private templateRef: TemplateRef<any>,
  ) {
    const _permission = this.store.selectSnapshot(
      PermissionsState.getActiveRouteAccessLevel,
    );
    if (_permission >= AccessRightLevelEnum.EditAccess) {
      this.vcr.createEmbeddedView(this.templateRef);
    } else {
      this.vcr.clear();
    }
  }
}

@Directive({
  selector: '[appHasDeveloperAccess]',
})
export class DeveloperAccessPermissionDirective {
  constructor(
    private store: Store,
    private vcr: ViewContainerRef,
    private templateRef: TemplateRef<any>,
  ) {
    const _permission = this.store.selectSnapshot(
      PermissionsState.getActiveRouteAccessLevel,
    );
    if (_permission >= AccessRightLevelEnum.DeveloperAccess) {
      this.vcr.createEmbeddedView(this.templateRef);
    } else {
      this.vcr.clear();
    }
  }
}

@Directive({
  selector: '[appHasViewAccess]',
})
export class ViewAccessPermissionDirective {
  constructor(
    private store: Store,
    private vcr: ViewContainerRef,
    private templateRef: TemplateRef<any>,
  ) {
    const _permission = this.store.selectSnapshot(
      PermissionsState.getActiveRouteAccessLevel,
    );

    if (_permission >= AccessRightLevelEnum.ViewAccess) {
      this.vcr.createEmbeddedView(this.templateRef);
    } else {
      this.vcr.clear();
    }
  }
}

@Directive({
  selector: '[appHasNoneAccess]',
})
export class NoneAccessPermissionDirective {
  constructor(
    private store: Store,
    private vcr: ViewContainerRef,
    private templateRef: TemplateRef<any>,
  ) {
    const _permission = this.store.selectSnapshot(
      PermissionsState.getActiveRouteAccessLevel,
    );

    if (_permission == AccessRightLevelEnum.None) {
      this.vcr.createEmbeddedView(this.templateRef);
    } else {
      this.vcr.clear();
    }
  }
}

@Directive({
  selector: '[appHasAdminAccess]',
})
export class AdminAccessPermissionDirective {
  constructor(
    private store: Store,
    private vcr: ViewContainerRef,
    private templateRef: TemplateRef<any>,
  ) {
    const _permission = this.store.selectSnapshot(
      PermissionsState.getActiveRouteAccessLevel,
    );

    if (_permission >= AccessRightLevelEnum.AdminAccess) {
      this.vcr.createEmbeddedView(this.templateRef);
    } else {
      this.vcr.clear();
    }
  }
}

@Directive({
  selector: '[appHasAdminLevelOf]',
})
export class AccessPermissionDirective {
  /**
   * On sidebar we need to check the admin level not the current access level so, we will add this as an input
   * This will identify the required admin level for specified element and compared to the user admin access level
   */
  @Input() set appHasAdminLevelOf(value: AccessRightLevelEnum | null) {
    if (!isNaN(value)) {
      const _permission = this.store.selectSnapshot(
        PermissionsState.getCurrentAdminLevel,
      );
      if (_permission >= value) {
        this.vcr.createEmbeddedView(this.templateRef);
        return;
      }
    }
    this.vcr.clear();
  }

  constructor(
    private store: Store,
    private vcr: ViewContainerRef,
    private templateRef: TemplateRef<any>,
  ) {}
}

@Directive({
  selector: '[appHasLevelOf]',
})
export class ActivatedRoutePermissionDirective {
  @Input() set appHasLevelOf(value: AccessRightLevelEnum) {
    if (!value) value = AccessRightLevelEnum.ViewAccess;
    if (!isNaN(value)) {
      const _permission = this.store.selectSnapshot(
        PermissionsState.getActiveRouteAccessLevel,
      );
      if (_permission >= value) {
        this.vcr.createEmbeddedView(this.templateRef);
        return;
      }
    }

    this.vcr.clear();
  }

  constructor(
    private store: Store,
    private vcr: ViewContainerRef,
    private templateRef: TemplateRef<any>,
  ) {}
}

@Directive({
  selector: '[appHasModuleEditAccess]',
})
export class ModuleEditAccessPermissionDirective {
  /**
   * This Directive will be used to check the module action
   */
  @Input() set appHasModuleEditAccess(value: string) {
    const userModuleTopPermission =
      this.accessHelper.getUserTopModuleLevel(value);
    if (userModuleTopPermission >= AccessRightLevelEnum.EditAccess) {
      this.vcr.createEmbeddedView(this.templateRef);
      return;
    }
    this.vcr.clear();
  }

  constructor(
    private vcr: ViewContainerRef,
    private templateRef: TemplateRef<any>,
    private accessHelper: AccessPermissionService,
  ) {}
}

@Directive({
  selector: '[appHasEntityEditAccess]',
})
export class EntityEditAccessPermissionDirective {
  @Input() set appHasEntityEditAccess(value: DataEntityPresenterModel) {
    if (this.accessHelper.hasEntityEditAccess(value.module, value.id)) {
      this.vcr.createEmbeddedView(this.templateRef);
      return;
    }
    this.vcr.clear();
  }

  constructor(
    private vcr: ViewContainerRef,
    private templateRef: TemplateRef<any>,
    private accessHelper: AccessPermissionService,
  ) {}
}
